// src/msgraph.js
// Microsoft Graph integration: sign in, send email on behalf of the signed-in
// user. Uses @azure/msal-browser (npm package, bundled into main.js).
//
// FLOW:
//   1. Rep clicks "Send via Outlook" in Compose Email modal
//   2. If no token cached -> popup window opens M365 sign-in
//      Admin pre-consented org-wide, so no consent screen for individual users
//   3. Token cached in localStorage (managed by MSAL itself)
//   4. Future sends are silent (token refresh handled by MSAL)
//   5. We POST to https://graph.microsoft.com/v1.0/me/sendMail
//
// THE BCC TRICK:
//   Every outgoing email includes a hidden BCC to the HubSpot logging address
//   (HUBSPOT_BCC below). HubSpot's inbox parser ingests it, creates the
//   activity record, and auto-associates to the contact by their email address.
//   Recipient never sees the BCC -- it's stripped from the visible headers.
//
// WHY NOT CDN:
//   An earlier version of this file dynamically loaded MSAL from
//   alcdn.msauth.net. That failed in production because Azure Static Web
//   Apps' Content Security Policy restricts script-src to 'self'. The npm
//   import path bundles MSAL into our own main.js, served from our origin,
//   which CSP allows.

import { PublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";

// ── Configuration ────────────────────────────────────────────────────────────
const CLIENT_ID      = "0f9a671e-369a-4c21-b888-366a758f4a57";
const TENANT_ID      = "391be3bc-9d6a-4607-8717-e377e8f6b2c0";
const AUTHORITY      = "https://login.microsoftonline.com/" + TENANT_ID;
const SCOPES         = ["Mail.Send"];
const HUBSPOT_BCC    = "24299314@bcc.hubspot.com";
const GRAPH_SENDMAIL = "https://graph.microsoft.com/v1.0/me/sendMail";

// ── MSAL instance (lazy singleton) ───────────────────────────────────────────
// Create on first use rather than at module load. MSAL v3 requires explicit
// initialize() before any other call, so we wrap creation in an async getter.
let _pca = null;
async function getMsal() {
  if (_pca) return _pca;
  _pca = new PublicClientApplication({
    auth: {
      clientId:    CLIENT_ID,
      authority:   AUTHORITY,
      redirectUri: window.location.origin,  // e.g. https://crm.abminternational.com
    },
    cache: {
      cacheLocation: "localStorage",  // persists across page reloads & tabs
      storeAuthStateInCookie: false,
    },
  });
  await _pca.initialize();
  return _pca;
}

// ── Public API ───────────────────────────────────────────────────────────────

// Returns the currently signed-in M365 account object (with username, name)
// or null. Safe to call anytime; never throws.
export async function getSignedInAccount() {
  try {
    const pca = await getMsal();
    const accounts = pca.getAllAccounts();
    return accounts[0] || null;
  } catch {
    return null;
  }
}

// Trigger the M365 sign-in popup. Returns the account object on success.
// Throws if the user cancels or sign-in fails.
export async function signIn() {
  const pca = await getMsal();
  const result = await pca.loginPopup({
    scopes: SCOPES,
    prompt: "select_account",  // lets user choose which M365 account to use
  });
  return result.account;
}

// Sign out the current account. Clears local MSAL cache.
export async function signOut() {
  try {
    const pca = await getMsal();
    const account = pca.getAllAccounts()[0];
    if (account) {
      await pca.logoutPopup({ account, postLogoutRedirectUri: window.location.origin });
    }
  } catch (e) {
    console.warn("M365 signOut failed:", e.message);
  }
}

// Acquire an access token silently (using cached refresh token). Falls back
// to interactive popup if MSAL says interaction is required. Throws if the
// user isn't signed in at all.
async function getAccessToken() {
  const pca = await getMsal();
  const account = pca.getAllAccounts()[0];
  if (!account) {
    const err = new Error("Not signed in to Microsoft");
    err.code = "NO_ACCOUNT";
    throw err;
  }
  try {
    const result = await pca.acquireTokenSilent({ scopes: SCOPES, account });
    return result.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      const result = await pca.acquireTokenPopup({ scopes: SCOPES, account });
      return result.accessToken;
    }
    throw e;
  }
}

// Send an email via Microsoft Graph as the signed-in user. The recipient
// sees the email come from the user's real M365 address. A hidden BCC to
// the HubSpot logging address (HUBSPOT_BCC) ensures HubSpot also gets a
// copy and records it on the contact.
//
// PARAMS:
//   to      - recipient email address (string)
//   subject - email subject line
//   body    - email body (plain text; line breaks preserved)
//
// THROWS on any failure -- caller shows error to user.
export async function sendMail({to, subject, body}) {
  if (!to)      throw new Error("Recipient is required");
  if (!subject) throw new Error("Subject is required");
  if (!body)    throw new Error("Body is required");

  const token = await getAccessToken();

  // Graph sendMail payload. contentType:"Text" tells Outlook to render the
  // body as plain text -- no HTML interpretation, line breaks preserved.
  const payload = {
    message: {
      subject: subject,
      body: { contentType: "Text", content: body },
      toRecipients:  [{ emailAddress: { address: to } }],
      bccRecipients: [{ emailAddress: { address: HUBSPOT_BCC } }],
    },
    saveToSentItems: true,  // email appears in rep's Outlook Sent folder
  };

  const res = await fetch(GRAPH_SENDMAIL, {
    method: "POST",
    headers: {
      "Authorization": "Bearer " + token,
      "Content-Type":  "application/json",
    },
    body: JSON.stringify(payload),
  });

  // Graph returns 202 Accepted on success with no body
  if (res.status === 202) return true;

  // On failure, surface Graph's error message if we can parse it
  let errMsg = "Send failed (HTTP " + res.status + ")";
  try {
    const errData = await res.json();
    if (errData?.error?.message) errMsg = errData.error.message;
  } catch {}
  throw new Error(errMsg);
}
